import { useCallback } from "react";
import { useTranslation } from "@/shared/i18n/LocaleProvider";

const NUMBER_FORMAT_LOCALE = "sv-SE";

function intlLocaleFor(appLocale: string | undefined): string {
  const base = (appLocale ?? "en").split("-")[0];
  return base === "en" ? "en-GB" : "sv-SE";
}

export function formatGroupedNumber(value: number, options?: Intl.NumberFormatOptions): string {
  if (!Number.isFinite(value)) return "";
  return new Intl.NumberFormat(NUMBER_FORMAT_LOCALE, options).format(value);
}

export function formatAmountSek(value: number): string {
  return `${formatGroupedNumber(Math.round(value), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}\u00a0SEK`;
}

function formatRelativeTimestamp(
  ts: number,
  now: Date,
  intl: string,
  yesterdayLabel: string,
): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";

  const startOfDay = (date: Date) => {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };
  const dayMs = 24 * 60 * 60 * 1000;
  const dayDiff = Math.round((startOfDay(now).getTime() - startOfDay(d).getTime()) / dayMs);

  if (dayDiff === 0) {
    return new Intl.DateTimeFormat(intl, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }

  if (dayDiff === 1) return yesterdayLabel;

  if (dayDiff > 1 && dayDiff < 7) {
    const weekday = new Intl.DateTimeFormat(intl, { weekday: "short" })
      .format(d)
      .replace(/\.$/, "");
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }

  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return new Intl.DateTimeFormat(intl, opts).format(d);
}

export function useFmtRelativeDate(): (ts: number) => string {
  const { t, locale } = useTranslation();
  const intl = intlLocaleFor(locale);
  return useCallback(
    (ts: number) => formatRelativeTimestamp(ts, new Date(), intl, t("common.yesterday")),
    [intl, t],
  );
}
